import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";

/** Per-field validator — returns error string or undefined (valid) */
type FieldValidator<T, K extends keyof T> = (
  value: T[K],
  form: T,
) => string | undefined;

/** Map of field names to their validators (single or array) */
export type ValidatorMap<T> = {
  [K in keyof T]?: FieldValidator<T, K> | FieldValidator<T, K>[];
};

/** Per-field error state — use errors.fieldName to get the error string */
type FormErrors<T> = { -readonly [K in keyof T]?: string };

function runFieldValidators<T, K extends keyof T>(
  value: T[K],
  form: T,
  validator: FieldValidator<T, K> | FieldValidator<T, K>[],
): string | undefined {
  if (Array.isArray(validator)) {
    for (const v of validator) {
      const result = v(value, form);
      if (result) return result;
    }
    return undefined;
  }
  return validator(value, form);
}

// ── Branded FormValidation type ──────────────────────────────────

declare const __formValidation: unique symbol;

/**
 * Branded validation object returned by `useFormValidation`.
 * Pass directly to `Form`'s `validation` prop — the brand prevents
 * constructing one manually, ensuring every Form is backed by real validation.
 */
export interface FormValidation<T = unknown> {
  readonly [__formValidation]: true;
  readonly validate: () => boolean;
  readonly submitted: boolean;
  /** The form data, typed as Required<T> after validation passes. */
  readonly validatedForm: Required<T>;
}

/** Use for Forms that don't need field validation. */
export const noValidation = {
  validate: () => true,
  submitted: false,
  validatedForm: undefined,
} as unknown as FormValidation; // safe-cast — branded type factory: cast is intentional to prevent ad-hoc construction

// ── Hook ─────────────────────────────────────────────────────────

export function useFormValidation<T extends object>(
  form: T,
  validators: ValidatorMap<T>,
): {
  /** Per-field errors — plain string | undefined. Pass to FormItem error prop. */
  errors: FormErrors<T>;
  /** Pass to Form's validation prop. Form calls validate() before onSave. */
  validation: FormValidation<T>;
  /** True if any field has an error (regardless of visibility) */
  hasErrors: boolean;
} {
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const result: FormErrors<T> = {};
    for (const key of Object.keys(validators) as (keyof T)[]) {
      const validator = validators[key];
      if (validator) {
        result[key] = runFieldValidators(form[key], form, validator);
      }
    }
    return result;
  }, [form, validators]);

  const hasErrors = useMemo(
    () => Object.values(errors).some((e) => !!e),
    [errors],
  );

  const validate = useCallback(() => {
    setSubmitted(true);

    const firstError = Object.values(errors).find((e) => !!e) as
      | string
      | undefined;
    if (firstError) {
      toast.error(firstError);
      return false;
    }
    return true;
  }, [errors]);

  const validation = useMemo(
    () => ({ validate, submitted, validatedForm: form }) as FormValidation<T>,
    [validate, submitted, form],
  );

  return { errors, validation, hasErrors };
}

// ── Built-in validators ─────────────────────────────────────────

/** Field is required — works for strings, numbers, and nullish values */
export const required =
  (label: string) =>
  (v: unknown): string | undefined =>
    !v || (typeof v === "string" && !v.trim())
      ? `${label} is required`
      : undefined;

/** Number must be positive (> 0) */
export const positive =
  (label?: string) =>
  (v: number | undefined): string | undefined =>
    v === undefined || v <= 0
      ? `${label ?? "Value"} must be positive`
      : undefined;

/** Number must be at least min */
export const minValue =
  (min: number, label?: string) =>
  (v: number | undefined): string | undefined =>
    v === undefined || v < min
      ? `${label ?? "Value"} must be at least ${min}`
      : undefined;

/** Number must be at most max */
export const maxValue =
  (max: number, label?: string) =>
  (v: number | undefined): string | undefined =>
    v !== undefined && v > max
      ? `${label ?? "Value"} must be at most ${max}`
      : undefined;

/** Permissive email format — empty/nullish passes, compose with `required` for mandatory fields. */
export const email =
  (label: string = "Email") =>
  (v: unknown): string | undefined => {
    if (!v) return undefined;
    if (typeof v !== "string" || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      return `${label} must be a valid email address`;
    }
    return undefined;
  };

/** Phone number — strips common formatting, requires 7-15 digits (NSN min through E.164 max). Empty/nullish passes. */
export const phoneNumber =
  (label: string = "Phone") =>
  (v: unknown): string | undefined => {
    if (!v) return undefined;
    if (typeof v !== "string") {
      return `${label} must be a valid phone number`;
    }
    const stripped = v.replace(/[-\s().+]/g, "");
    if (!/^\d{7,15}$/.test(stripped)) {
      return `${label} must be a valid phone number`;
    }
    return undefined;
  };
